// ============================================================================
// AVATAR BUILDER COMPONENT
// Tabbed interface for creating and editing avatars
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, X, ChevronRight, ChevronLeft, AlertCircle, Info, GitBranch, Loader2, RefreshCw, Bot } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  AvatarCategory,
  AvatarScope,
  AvatarBackendType,
  TTSProvider,
  CreateAvatarInput,
  UpdateAvatarInput,
  CognitiveElement,
  AvatarCognitivePolicy
} from '../types';
import {
  createAvatar,
  updateAvatar,
  getAvatar,
  generateCatalogAvatarImage,
  setCatalogAvatarImage
} from '../services/avatarCatalogService';
import AvatarKBSection from './AvatarKBSection';
import StringListEditor from './common/StringListEditor';
import TaggedListEditor from './common/TaggedListEditor';
import VoiceSelector from './VoiceSelector';

type TabId = 'basic' | 'behavior' | 'metadata' | 'voice' | 'knowledge' | 'settings';

interface AvatarBuilderProps {
  mode: 'create' | 'edit';
}

export default function AvatarBuilder({ mode }: AvatarBuilderProps) {
  const navigate = useNavigate();
  const { avatarId } = useParams<{ avatarId: string }>();
  const { user, can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab from URL, validate it's a valid TabId
  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const validTabs: TabId[] = ['basic', 'behavior', 'metadata', 'voice', 'knowledge', 'settings'];
  const initialTab = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'basic';

  const [activeTab, setActiveTab] = useState<TabId>(initialTab);

  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);
  const [loading, setLoading] = useState(false);
  const [ownerIds, setOwnerIds] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<AvatarCategory>(AvatarCategory.CUSTOM);
  const [scope, setScope] = useState<AvatarScope>(AvatarScope.USER);

  const [systemPrompt, setSystemPrompt] = useState('');
  const [backendType, setBackendType] = useState<AvatarBackendType>(AvatarBackendType.PROMPT);
  const [knowledgeBaseId, setKnowledgeBaseId] = useState('');
  const [retrievalMaxResults, setRetrievalMaxResults] = useState(5);
  const [minRelevanceScore, setMinRelevanceScore] = useState(0.7);

  const [keywords, setKeywords] = useState<string[]>([]);
  const [maxResponseLength, setMaxResponseLength] = useState(50);

  // Cognitive policy — reasoning method (goals/lenses/mandatory questions/evidence
  // rules/decision criteria), distinct from systemPrompt (identity/tone only).
  const [cogGoals, setCogGoals] = useState<string[]>([]);
  const [cogLenses, setCogLenses] = useState<CognitiveElement[]>([]);
  const [cogMandatoryQuestions, setCogMandatoryQuestions] = useState<CognitiveElement[]>([]);
  const [cogEvidenceRules, setCogEvidenceRules] = useState<CognitiveElement[]>([]);
  const [cogDecisionCriteria, setCogDecisionCriteria] = useState<CognitiveElement[]>([]);

  const [defaultModelId, setDefaultModelId] = useState('');

  const [ttsVoiceId, setTtsVoiceId] = useState(mode === 'create' ? '21m00Tcm4TlvDq8ikWAM' : '');

  const [supportsPhysicalDevice, setSupportsPhysicalDevice] = useState(true);
  const [isPublic, setIsPublic] = useState(false);

  // Image generation — "Accept" persists immediately via setCatalogAvatarImage
  // (an in-place field update), not via handleSave/updateAvatar, which would
  // create an unwanted new version row just to change one field.
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [pendingImageS3Key, setPendingImageS3Key] = useState<string | null>(null);
  const [savingImage, setSavingImage] = useState(false);


  const loadAvatar = useCallback(async () => {
    if (!avatarId) return;

    try {
      setLoading(true);
      const result = await getAvatar(avatarId);
      const avatar = result.avatar;
      setOwnerIds([avatar.ownerId, avatar.createdBy]);

      setName(avatar.name);
      setDescription(avatar.description);
      setCategory(avatar.category);
      setScope(avatar.scope);
      setSystemPrompt(avatar.systemPrompt);
      setBackendType(avatar.backendType);
      setKnowledgeBaseId(avatar.knowledgeBaseId || '');
      setRetrievalMaxResults(avatar.knowledgeBaseConfig?.retrievalMaxResults || 5);
      setMinRelevanceScore(avatar.knowledgeBaseConfig?.minRelevanceScore || 0.7);
      setKeywords(avatar.keywords);
      setMaxResponseLength(avatar.maxResponseLength);
      setCogGoals(avatar.cognitivePolicy?.goals || []);
      setCogLenses(avatar.cognitivePolicy?.lenses || []);
      setCogMandatoryQuestions(avatar.cognitivePolicy?.mandatoryQuestions || []);
      setCogEvidenceRules(avatar.cognitivePolicy?.evidenceRules || []);
      setCogDecisionCriteria(avatar.cognitivePolicy?.decisionCriteria || []);
      setDefaultModelId(avatar.defaultModelId || '');
      setTtsVoiceId(avatar.ttsVoiceId || '');
      setSupportsPhysicalDevice(avatar.supportsPhysicalDevice);
      setIsPublic(avatar.isPublic || false);
      setImageUrl(avatar.imageUrl);
    } catch (err: any) {
      setError(err.message || 'Failed to load avatar');
    } finally {
      setLoading(false);
    }
  }, [avatarId]);

  // Saves are refused unless you own the avatar or hold ManageAnyDefinition
  // (avatar-catalog.ts), so don't offer a form that can only fail.
  const mayEdit = !ownerIds || (!!user && ownerIds.includes(user.userId)) || can('Avatars::ManageAnyDefinition');
  useEffect(() => {
    if (mode === 'edit' && !mayEdit) navigate('/unauthorized', { replace: true });
  }, [mode, mayEdit, navigate]);

  // Load existing avatar in edit mode
  useEffect(() => {
    if (mode === 'edit' && avatarId) {
      loadAvatar();
    }
  }, [mode, avatarId, loadAvatar]);

  const validateForm = (): string | null => {
    if (!name.trim()) return 'Name is required';
    if (name.length < 3) return 'Name must be at least 3 characters';
    if (!description.trim()) return 'Description is required';
    if (description.length < 10) return 'Description must be at least 10 characters';
    if (!systemPrompt.trim()) return 'System prompt is required';
    if (systemPrompt.length < 20) return 'System prompt must be at least 20 characters';
    if (keywords.length === 0) return 'At least one keyword is required';
    if (maxResponseLength < 20 || maxResponseLength > 20000) {
      return 'Max response length must be between 20 and 20000 characters';
    }
    if (backendType === AvatarBackendType.BEDROCK_KNOWLEDGE_BASE && !knowledgeBaseId.trim()) {
      return 'Knowledge Base ID is required for Knowledge Base backend';
    }
    const hasCognitiveContent = cogGoals.length > 0 || cogLenses.length > 0
      || cogMandatoryQuestions.length > 0 || cogEvidenceRules.length > 0 || cogDecisionCriteria.length > 0;
    if (hasCognitiveContent && cogGoals.length === 0) {
      return 'Cognitive policy requires at least one goal';
    }
    if (hasCognitiveContent && cogMandatoryQuestions.length === 0) {
      return 'Cognitive policy requires at least one mandatory question';
    }
    return null;
  };

  const buildCognitivePolicy = (): AvatarCognitivePolicy | undefined => {
    const hasCognitiveContent = cogGoals.length > 0 || cogLenses.length > 0
      || cogMandatoryQuestions.length > 0 || cogEvidenceRules.length > 0 || cogDecisionCriteria.length > 0;
    if (!hasCognitiveContent) return undefined;
    return {
      goals: cogGoals,
      lenses: cogLenses,
      mandatoryQuestions: cogMandatoryQuestions,
      evidenceRules: cogEvidenceRules,
      decisionCriteria: cogDecisionCriteria
    };
  };

  const getKnowledgeBaseFields = () => ({
    knowledgeBaseId: backendType === AvatarBackendType.BEDROCK_KNOWLEDGE_BASE ? knowledgeBaseId : undefined,
    knowledgeBaseConfig: backendType === AvatarBackendType.BEDROCK_KNOWLEDGE_BASE
      ? { retrievalMaxResults, minRelevanceScore }
      : undefined
  });

  const buildCreateInput = (): CreateAvatarInput => ({
    name: name.trim(),
    description: description.trim(),
    systemPrompt: systemPrompt.trim(),
    category,
    scope,
    createdBy: user?.userId || '',
    ownerId: user?.userId || '',
    backendType,
    ...getKnowledgeBaseFields(),
    cognitivePolicy: buildCognitivePolicy(),
    keywords,
    maxResponseLength,
    defaultModelId: defaultModelId.trim() || undefined,
    ttsProvider: TTSProvider.ELEVENLABS,
    ttsVoiceId: ttsVoiceId.trim() || undefined,
    ttsConfig: {},
    supportsPhysicalDevice,
    isPublic
  });

  const buildUpdateInput = (): UpdateAvatarInput => ({
    name: name.trim(),
    description: description.trim(),
    systemPrompt: systemPrompt.trim(),
    category,
    cognitivePolicy: buildCognitivePolicy(),
    keywords,
    maxResponseLength,
    defaultModelId: defaultModelId.trim() || undefined,
    ttsProvider: TTSProvider.ELEVENLABS,
    ttsVoiceId: ttsVoiceId.trim() || undefined,
    ttsConfig: {},
    supportsPhysicalDevice,
    isPublic,
    ...getKnowledgeBaseFields(),
    kbConfig: { enabled: true, useSharedCollection: true, autoQueryDuringSessions: true }
  });

  const handleGenerateImage = async () => {
    if (!name.trim() || !description.trim()) {
      setError('Name and description are required to generate an image.');
      return;
    }
    setError(null);
    setGeneratingImage(true);
    setPreviewImageUrl(null);
    try {
      const result = await generateCatalogAvatarImage(avatarId!, name.trim(), description.trim());
      setPreviewImageUrl(result.imageUrl);
      setPendingImageS3Key(result.imageS3Key);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Image generation failed');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleAcceptImage = async () => {
    if (!previewImageUrl || !pendingImageS3Key) return;
    setSavingImage(true);
    setError(null);
    try {
      await setCatalogAvatarImage(avatarId!, pendingImageS3Key);
      setImageUrl(previewImageUrl);
      setPreviewImageUrl(null);
      setPendingImageS3Key(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save image');
    } finally {
      setSavingImage(false);
    }
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (mode === 'create') {
        const input = buildCreateInput();

        const result = await createAvatar(input);
        alert(`Avatar "${result.avatar.name}" created successfully!`);
        navigate('/avatars');
      } else if (mode === 'edit' && avatarId) {
        const changeNotes = window.prompt('Describe your changes (required):');
        if (!changeNotes) {
          alert('Change notes are required to update an avatar');
          return;
        }

        const updates = buildUpdateInput();

        const result = await updateAvatar(avatarId, updates, changeNotes);
        alert(`Avatar updated successfully! New version: ${result.avatar.versionNumber}`);
        navigate('/avatars');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save avatar');
    } finally {
      setSaving(false);
    }
  };

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'behavior', label: 'Behavior' },
    { id: 'metadata', label: 'Metadata' },
    { id: 'voice', label: 'Voice' },
    { id: 'knowledge', label: 'Knowledge Base' },
    { id: 'settings', label: 'Settings' }
  ];

  if (loading || (mode === 'edit' && !mayEdit)) {
    return (
      <div className="flex justify-center items-center py-20 text-text-sub">
        <div className="text-sm font-medium">Loading avatar...</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <h2 className="text-3xl font-bold text-text-main">
            {mode === 'create' ? 'Create Avatar' : 'Edit Avatar'}
          </h2>
          <p className="text-text-sub text-sm mt-1">
            {mode === 'create'
              ? 'Configure a new AI avatar for your workshops'
              : `Editing: ${name || 'Unnamed Avatar'}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/avatars')}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-text-main font-medium rounded-xl hover:bg-slate-50 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-text-main font-bold px-5 py-2.5 rounded-xl transition-all shadow-md shadow-primary/20 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Avatar'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <AlertCircle size={16} />
            <span><strong>Error:</strong> {error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 transition-colors">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-8 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 text-sm font-medium rounded-t-xl transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-navy text-white'
                : 'text-text-sub hover:bg-slate-50 hover:text-text-main'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Avatar Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Devil's Advocate, Customer Champion"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400"
              />
              <p className="text-xs text-text-sub mt-1.5">Minimum 3 characters. This will be displayed to users.</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe this avatar's role and purpose..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400 resize-y"
              />
              <p className="text-xs text-text-sub mt-1.5">Minimum 10 characters. Helps users understand when to use this avatar.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-text-main mb-2">Category *</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as AvatarCategory)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer"
                >
                  <option value={AvatarCategory.FACILITATOR}>Facilitator</option>
                  <option value={AvatarCategory.DOMAIN_EXPERT}>Domain Expert</option>
                  <option value={AvatarCategory.STAKEHOLDER}>Stakeholder</option>
                  <option value={AvatarCategory.RESEARCHER}>Researcher</option>
                  <option value={AvatarCategory.CUSTOM}>Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-text-main mb-2">Scope *</label>
                <select
                  value={scope}
                  onChange={(e) => setScope(e.target.value as AvatarScope)}
                  disabled={mode === 'edit'}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option value={AvatarScope.SESSION}>Session (ephemeral)</option>
                  <option value={AvatarScope.USER}>User (personal library)</option>
                  <option value={AvatarScope.ORGANIZATION}>Organization (shared)</option>
                </select>
                {mode === 'edit' && (
                  <p className="text-xs text-text-sub mt-1.5">Scope cannot be changed after creation</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Portrait Image</label>
              {mode === 'create' ? (
                <p className="text-xs text-text-sub">Save the avatar first to generate a portrait image.</p>
              ) : generatingImage ? (
                <div className="flex items-center gap-2.5 p-4 border border-dashed border-slate-200 rounded-xl text-text-sub text-sm">
                  <Loader2 size={16} className="animate-spin flex-shrink-0" />
                  Generating sketchbook portrait… this takes 15–30s
                </div>
              ) : previewImageUrl ? (
                <div>
                  <img src={previewImageUrl} alt="Generated avatar preview" className="w-full max-h-[200px] object-cover rounded-xl border border-slate-200 block" />
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={handleAcceptImage}
                      disabled={savingImage}
                      className="flex-1 py-2 bg-primary text-text-main rounded-lg text-sm font-semibold disabled:opacity-60"
                    >
                      {savingImage ? 'Saving...' : 'Accept'}
                    </button>
                    <button
                      type="button"
                      onClick={handleGenerateImage}
                      disabled={savingImage}
                      className="flex items-center justify-center gap-1.5 px-3.5 py-2 bg-white border border-slate-200 text-text-main rounded-lg text-sm font-semibold disabled:opacity-60"
                    >
                      <RefreshCw size={12} /> Regen
                    </button>
                  </div>
                </div>
              ) : imageUrl ? (
                <div>
                  <img src={imageUrl} alt="Avatar portrait" className="w-full max-h-[200px] object-cover rounded-xl border border-slate-200 block" />
                  <button
                    type="button"
                    onClick={handleGenerateImage}
                    className="flex items-center gap-1.5 mt-1.5 text-xs font-semibold text-text-sub bg-transparent border-none cursor-pointer py-0.5"
                  >
                    <RefreshCw size={11} /> Regenerate image
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleGenerateImage}
                  className="w-full p-5 border-[1.5px] border-dashed border-slate-200 rounded-xl bg-slate-50 cursor-pointer flex flex-col items-center gap-1.5 text-slate-400"
                >
                  <Bot size={24} className="text-slate-300" />
                  <span className="text-xs font-medium">Click to generate a sketchbook portrait</span>
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'behavior' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">System Prompt *</label>
              <textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="You are a [role]. Your goal is to [objective]. You should [behavior guidelines]..."
                rows={8}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main font-mono focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400 resize-y"
              />
              <p className="text-xs text-text-sub mt-1.5">Minimum 20 characters. This defines the avatar's persona and behavior.</p>
            </div>

            <div className="border-t border-slate-200 pt-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2">
                    <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-blue-800 mb-1">Cognitive Policy (optional)</h3>
                      <p className="text-xs text-blue-700 leading-relaxed">
                        Beyond the system prompt above, define how this avatar reasons: what it's trying to
                        achieve, the perspectives it applies, what it must always raise, its evidence
                        standards, and what it weighs when taking a position. Lenses, questions, evidence
                        rules, and decision criteria can be tagged with topics — they only activate when the
                        conversation is actually about that topic. Leave everything blank to keep this avatar
                        on its system prompt alone.
                      </p>
                    </div>
                  </div>
                  {mode === 'edit' && avatarId && (
                    <button
                      type="button"
                      onClick={() => navigate(`/avatars/${avatarId}/cognition-graph`)}
                      className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 flex-shrink-0 whitespace-nowrap"
                    >
                      <GitBranch size={13} />
                      View reasoning graph ↗
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <StringListEditor
                  label="Goals (what this avatar is trying to achieve)"
                  items={cogGoals}
                  onChange={setCogGoals}
                  placeholder="Enter a goal and press Enter..."
                  hint="Always active — not topic-gated."
                />

                <TaggedListEditor
                  label="Lenses (perspectives it habitually applies)"
                  items={cogLenses}
                  onChange={setCogLenses}
                  textPlaceholder="e.g., Reality-check technical complexity and timelines"
                />

                <TaggedListEditor
                  label="Mandatory Questions (must raise/consider before answering)"
                  items={cogMandatoryQuestions}
                  onChange={setCogMandatoryQuestions}
                  textPlaceholder="e.g., What fails independently?"
                />

                <TaggedListEditor
                  label="Evidence Rules"
                  items={cogEvidenceRules}
                  onChange={setCogEvidenceRules}
                  textPlaceholder="e.g., Don't invent evidence — cite the KB by name or label as an assumption"
                />

                <TaggedListEditor
                  label="Decision Criteria (what it weighs when taking a position)"
                  items={cogDecisionCriteria}
                  onChange={setCogDecisionCriteria}
                  textPlaceholder="e.g., Simplicity of the path to value"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Backend Type *</label>
              <select
                value={backendType}
                onChange={(e) => setBackendType(e.target.value as AvatarBackendType)}
                disabled={mode === 'edit'}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value={AvatarBackendType.PROMPT}>Prompt-based (Standard LLM)</option>
                <option value={AvatarBackendType.BEDROCK_AGENT}>Bedrock Agent</option>
                <option value={AvatarBackendType.BEDROCK_KNOWLEDGE_BASE}>Bedrock Knowledge Base (RAG)</option>
              </select>
              {mode === 'edit' && (
                <p className="text-xs text-text-sub mt-1.5">Backend type cannot be changed after creation</p>
              )}
            </div>

            {backendType === AvatarBackendType.BEDROCK_KNOWLEDGE_BASE && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-text-main mb-2">Knowledge Base ID *</label>
                  <input
                    type="text"
                    value={knowledgeBaseId}
                    onChange={(e) => setKnowledgeBaseId(e.target.value)}
                    placeholder="e.g., KB12345ABCDE"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main font-mono focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400"
                  />
                  <p className="text-xs text-text-sub mt-1.5">AWS Bedrock Knowledge Base ID for RAG retrieval</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-text-main mb-2">Max Retrieval Results</label>
                    <input
                      type="number"
                      value={retrievalMaxResults}
                      onChange={(e) => setRetrievalMaxResults(parseInt(e.target.value))}
                      min={1}
                      max={20}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-text-main mb-2">Min Relevance Score</label>
                    <input
                      type="number"
                      value={minRelevanceScore}
                      onChange={(e) => setMinRelevanceScore(parseFloat(e.target.value))}
                      min={0}
                      max={1}
                      step={0.1}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'metadata' && (
          <div className="space-y-6">
            {/* LLM-Based Selection Explanation */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-blue-800 mb-1">Avatar Selection</h3>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    The orchestrator uses an LLM to intelligently determine which avatars should respond during each workshop phase based on the conversation context and the avatar's system prompt. Keywords below are used for search and organization only.
                  </p>
                </div>
              </div>
            </div>

            {/* Keywords */}
            <StringListEditor
              label="Keywords * (at least 1 required)"
              items={keywords}
              onChange={setKeywords}
              placeholder="Enter a keyword and press Enter..."
              hint="Keywords help users discover and filter avatars. They are not used for triggering during workshops."
            />

            {/* Max Response Length */}
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Max Response Length (words) *</label>
              <input
                type="number"
                value={maxResponseLength}
                onChange={(e) => setMaxResponseLength(parseInt(e.target.value))}
                min={20}
                max={4000}
                className="w-48 px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
              <p className="text-xs text-text-sub mt-1.5">Maximum length of avatar responses (20-4000 words). Keeps responses concise and focused.</p>
            </div>
          </div>
        )}

        {activeTab === 'voice' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Voice</label>
              <VoiceSelector value={ttsVoiceId} onChange={setTtsVoiceId} />
              <p className="text-xs text-text-sub mt-1.5">ElevenLabs voices from the managed list. System admins add voices under Admin &gt; Voices.</p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-text-main mb-3">Voice Configuration Tips</h3>
              <ul className="text-xs text-text-sub leading-relaxed space-y-1.5 list-disc pl-4">
                <li>Choose a voice that matches the avatar's personality and role</li>
                <li>Press the play button next to the dropdown to hear a sample before saving</li>
                <li>Voices are grouped by accent; pick one that suits the audience</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'knowledge' && (
          <div>
            {mode === 'edit' && avatarId ? (
              <AvatarKBSection avatarDefinitionId={avatarId} />
            ) : (
              <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl p-10 text-center">
                <p className="text-sm text-text-main font-medium mb-2">
                  Knowledge Base configuration is available after creating the avatar
                </p>
                <p className="text-xs text-text-sub">
                  Save this avatar first, then you can upload documents to its knowledge base
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={supportsPhysicalDevice}
                  onChange={(e) => setSupportsPhysicalDevice(e.target.checked)}
                  className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                />
                <div>
                  <span className="text-sm font-semibold text-text-main">Supports Physical Device</span>
                  <p className="text-xs text-text-sub mt-1">Enable if the avatar can be assigned to a Raspberry Pi device for in-person workshops</p>
                </div>
              </label>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                />
                <div>
                  <span className="text-sm font-semibold text-text-main">Make Public</span>
                  <p className="text-xs text-text-sub mt-1">Public avatars can be viewed and cloned by other users in your organization</p>
                </div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-semibold text-text-main mb-2">Default Model</label>
              <select
                value={defaultModelId}
                onChange={(e) => setDefaultModelId(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer bg-white"
              >
                <option value="">Use system default (Claude 3 Sonnet)</option>

                <optgroup label="Claude Models (Anthropic)">
                  <option value="eu.anthropic.claude-3-sonnet-20240229-v1:0">
                    Claude 3 Sonnet - Medium complexity ($3/$15 per 1M tokens)
                  </option>
                  <option value="anthropic.claude-3-haiku-20240307-v1:0">
                    Claude 3 Haiku - Low complexity ($0.25/$1.25 per 1M tokens)
                  </option>
                </optgroup>

                <optgroup label="Llama Models (Meta)">
                  <option value="eu.meta.llama3-2-3b-instruct-v1:0">
                    Llama 3.2 3B Instruct - Low complexity ($0.15/$0.20 per 1M tokens)
                  </option>
                  <option value="eu.meta.llama3-2-1b-instruct-v1:0">
                    Llama 3.2 1B Instruct - Minimal complexity ($0.10/$0.10 per 1M tokens)
                  </option>
                </optgroup>

                <optgroup label="GPT OSS Models (OpenAI)">
                  <option value="openai.gpt-oss-120b-1:0">
                    GPT OSS 120B - Medium complexity ($1/$2 per 1M tokens)
                  </option>
                  <option value="openai.gpt-oss-20b-1:0">
                    GPT OSS 20B - Low complexity ($0.30/$0.40 per 1M tokens)
                  </option>
                </optgroup>

                <optgroup label="Gemma Models (Google)">
                  <option value="google.gemma-3-4b-it">
                    Gemma 3 4B IT - Low complexity ($0.10/$0.15 per 1M tokens)
                  </option>
                  <option value="google.gemma-3-12b-it">
                    Gemma 3 12B IT - Medium complexity ($0.25/$0.35 per 1M tokens)
                  </option>
                  <option value="google.gemma-3-27b-it">
                    Gemma 3 27B PT - Medium complexity ($0.50/$0.75 per 1M tokens)
                  </option>
                </optgroup>
              </select>
              <p className="text-xs text-text-sub mt-1.5">
                Select a specific Bedrock model for this avatar. If not set, uses the system default (Claude 3 Sonnet).
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-amber-800 mb-2">Publishing to Organization Catalog</h3>
              <p className="text-xs text-amber-700 leading-relaxed">
                After creating your avatar, you can submit it for review to add it to the organization catalog.
                Organization admins will review your submission and may approve or reject it with feedback.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-10 pt-5 border-t border-slate-200">
        <button
          onClick={() => {
            const currentIndex = tabs.findIndex(t => t.id === activeTab);
            if (currentIndex > 0) {
              setActiveTab(tabs[currentIndex - 1].id);
            }
          }}
          disabled={activeTab === 'basic'}
          className="flex items-center gap-2 px-5 py-2.5 bg-navy text-white rounded-xl text-sm font-semibold transition-all disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed hover:bg-navy-dark"
        >
          <ChevronLeft size={16} />
          Previous
        </button>
        <button
          onClick={() => {
            const currentIndex = tabs.findIndex(t => t.id === activeTab);
            if (currentIndex < tabs.length - 1) {
              setActiveTab(tabs[currentIndex + 1].id);
            }
          }}
          disabled={activeTab === 'settings'}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary text-text-main rounded-xl text-sm font-semibold transition-all disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed hover:bg-primary/90"
        >
          Next
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
